import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Simple least-squares linear trend over evenly-spaced points (same categorical-x convention
// as the rest of this page's mini trend charts — spacing reflects test order, not calendar gaps).
function withTrend(points) {
  const n = points.length;
  if (n < 2) return points.map(p => ({ ...p, trend: p.value }));
  const meanX = (n - 1) / 2;
  const meanY = points.reduce((s, p) => s + p.value, 0) / n;
  let num = 0, den = 0;
  points.forEach((p, i) => { num += (i - meanX) * (p.value - meanY); den += (i - meanX) ** 2; });
  const slope = den === 0 ? 0 : num / den;
  const intercept = meanY - slope * meanX;
  return points.map((p, i) => ({ ...p, trend: slope * i + intercept }));
}

export default function PassingDrillDetailModal({ rows, onClose }) {
  const [selectedPlayerId, setSelectedPlayerId] = useState('');

  const players = useMemo(() => {
    const map = new Map();
    (rows || []).forEach(r => { if (r.player_id != null && !map.has(r.player_id)) map.set(r.player_id, r.player_name); });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const chartData = useMemo(() => {
    let points;
    if (selectedPlayerId) {
      points = (rows || [])
        .filter(r => String(r.player_id) === String(selectedPlayerId) && r.adjusted_pct_hrmax != null)
        .map(r => ({ date: (r.test_date || '').slice(0, 10), value: Number(r.adjusted_pct_hrmax) }));
    } else {
      const byDate = new Map();
      (rows || []).forEach(r => {
        if (r.adjusted_pct_hrmax == null) return;
        const date = (r.test_date || '').slice(0, 10);
        if (!byDate.has(date)) byDate.set(date, []);
        byDate.get(date).push(Number(r.adjusted_pct_hrmax));
      });
      points = Array.from(byDate, ([date, vals]) => ({ date, value: vals.reduce((a, b) => a + b, 0) / vals.length }));
    }
    points.sort((a, b) => a.date.localeCompare(b.date));
    return withTrend(points);
  }, [rows, selectedPlayerId]);

  const seriesLabel = selectedPlayerId ? players.find(p => String(p.id) === String(selectedPlayerId))?.name : 'Team Average';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 1100,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '32px 16px', overflowY: 'auto',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--bg-app)', borderRadius: 14, padding: '28px 32px',
        width: '100%', maxWidth: 900, border: '1px solid var(--border-strong)',
        boxShadow: '0 25px 60px rgba(0,0,0,0.55)', position: 'relative',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Adjusted Passing Drill %HRmax</h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{seriesLabel} — full history with trend line</p>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Show</label>
          <select value={selectedPlayerId} onChange={e => setSelectedPlayerId(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13, minWidth: 220 }}>
            <option value="">Team Average</option>
            {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={420}>
            <LineChart data={chartData}>
              <XAxis dataKey="date" stroke="var(--text-dimmed)" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
              <YAxis stroke="var(--text-dimmed)" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} domain={['auto', 'auto']} />
              <Tooltip contentStyle={{ background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: 8, fontSize: 12 }} formatter={v => Number(v).toFixed(1)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="value" name={seriesLabel} stroke="#06b6d4" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              <Line type="linear" dataKey="trend" name="Trend" stroke="#06b6d4" strokeWidth={2} strokeDasharray="6 4" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ height: 420, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dimmed)', fontSize: 13 }}>
            No data yet.
          </div>
        )}
      </div>
    </div>
  );
}
